import { useEffect, useState } from 'react'

const formatDate = value => value ? new Date(value).toLocaleString('en-NG', {
  timeZone: 'Africa/Lagos', dateStyle: 'medium', timeStyle: 'short'
}) + ' WAT' : 'Not yet synced'

export default function WalletSyncStatus({ apiBase }) {
  const [status, setStatus] = useState(null)
  const [phase, setPhase] = useState('syncing')

  useEffect(() => {
    let mounted = true
    let running = false
    let timer
    let failures = 0
    const readStatus = async () => {
      const response = await fetch(`${apiBase}/flw/sync-status`, { cache: 'no-store' })
      if (!response.ok) throw new Error('Unable to check wallet sync')
      const data = await response.json()
      if (mounted) setStatus(data)
      return data
    }
    const schedule = delay => {
      clearTimeout(timer)
      if (mounted) timer = setTimeout(run, delay)
    }
    const run = async () => {
      if (!mounted || running) return
      if (document.hidden) { schedule(60000); return }
      running = true
      setPhase('syncing')
      let delay = 300000
      try {
        await readStatus()
        if (!mounted) return
        const response = await fetch(`${apiBase}/flw/sync-wallet`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ auto: true }), signal: AbortSignal.timeout(150000)
        })
        const data = await response.json()
        if (!mounted) return
        setStatus(data)
        if (data.deferred) {
          setPhase(data.syncing ? 'syncing' : data.last_error ? 'retry' : 'idle')
          delay = data.syncing ? 10000 : Math.max(10000, Math.min(300000, data.retry_after - Date.now()))
          // Another open app may have finished importing since this page first loaded.
          window.dispatchEvent(new Event('wallet-synced'))
        } else if (!response.ok || !data.success) {
          // A successful side may have been imported even when the other side is unavailable.
          window.dispatchEvent(new Event('wallet-synced'))
          throw new Error(data.error || 'Wallet sync unavailable')
        } else {
          failures = 0
          setPhase('idle')
          window.dispatchEvent(new Event('wallet-synced'))
        }
      } catch {
        if (mounted) {
          setPhase('retry')
          failures++
          delay = Math.min(300000, 60000 * 2 ** Math.min(failures - 1, 3))
        }
      } finally {
        running = false
        schedule(delay)
      }
    }
    const resume = () => { if (!document.hidden) { clearTimeout(timer); run() } }
    const update = () => { readStatus().catch(() => {}) }
    document.addEventListener('visibilitychange', resume)
    window.addEventListener('online', resume)
    window.addEventListener('wallet-synced', update)
    run()
    return () => {
      mounted = false
      clearTimeout(timer)
      document.removeEventListener('visibilitychange', resume)
      window.removeEventListener('online', resume)
      window.removeEventListener('wallet-synced', update)
    }
  }, [apiBase])

  return (
    <div className="wallet-sync-status" role="status" title={`Wallet Income: ${formatDate(status?.income_synced_at)}\nWallet Expense: ${formatDate(status?.expense_synced_at)}`}>
      <span>{phase === 'syncing' ? 'Syncing wallet…' : phase === 'retry' ? 'Wallet sync unavailable · retrying automatically' : status?.last_synced_at ? 'Wallet synced' : 'Waiting for wallet sync'}</span>
      <span>Synced: {status?.last_synced_at ? <time dateTime={status.last_synced_at}>{formatDate(status.last_synced_at)}</time> : 'Not yet synced'}</span>
    </div>
  )
}
